import React, { useState } from 'react';
import { Card, Table, Spin, Empty, Typography, Space, Button } from 'antd';
import { BarChartOutlined, LineChartOutlined, PieChartOutlined, TableOutlined, SettingOutlined } from '@ant-design/icons';
import DataVisualization from '../common/DataVisualization';
import ChartConfigModal from '../common/ChartConfigModal';

const { Title } = Typography;

const PreviewPanel = ({
  data = [],
  loading = false,
  error = null,
  viewMode = 'table',
  onViewModeChange
}) => {
  const [chartConfig, setChartConfig] = useState(null);
  const [chartModalVisible, setChartModalVisible] = useState(false);

  const viewModes = [
    { key: 'table', icon: TableOutlined, label: 'Table' },
    { key: 'bar', icon: BarChartOutlined, label: 'Bar Chart' },
    { key: 'line', icon: LineChartOutlined, label: 'Line Chart' },
    { key: 'pie', icon: PieChartOutlined, label: 'Pie Chart' }
  ];

  const renderTableView = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <Empty
          description="No data to display"
          style={{ margin: '40px 0' }}
        />
      );
    }

    // Ensure first item is an object
    if (!data[0] || typeof data[0] !== 'object') {
      return (
        <Empty
          description="Invalid data format"
          style={{ margin: '40px 0' }}
        />
      );
    }

    // Get columns from first row
    const columns = Object.keys(data[0]).map(key => ({
      title: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
      dataIndex: key,
      key: key,
      ellipsis: true,
      width: 150
    }));

    return (
      <Table
        dataSource={data}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content', y: 300 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
        }}
      />
    );
  };

  const handleChartConfig = () => {
    setChartModalVisible(true);
  };

  const handleChartSave = (config) => {
    setChartConfig(config);
    setChartModalVisible(false);
  };

  const renderChartView = () => {
    if (!chartConfig) {
      return (
        <div style={{
          height: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          gap: '16px'
        }}>
          <div style={{ fontSize: '48px', color: '#d9d9d9' }}>
            📊
          </div>
          <Typography.Text type="secondary">
            Configure chart settings to visualize your data
          </Typography.Text>
          <Button
            type="primary"
            icon={<SettingOutlined />}
            onClick={handleChartConfig}
          >
            Configure Chart
          </Button>
        </div>
      );
    }

    return (
      <div style={{ height: '100%', position: 'relative' }}>
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10
        }}>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={handleChartConfig}
          >
            Configure
          </Button>
        </div>
        <DataVisualization
          data={data}
          chartType={chartConfig.chartType}
          xAxisField={chartConfig.xAxisField}
          yAxisField={chartConfig.yAxisField}
          title={chartConfig.title}
          height={chartConfig.height || 400}
        />
      </div>
    );
  };

  const renderContent = () => {
    if (error) {
      return (
        <Empty
          description={
            <span style={{ color: '#ff4d4f' }}>
              Error loading data: {error}
            </span>
          }
          style={{ margin: '40px 0' }}
        />
      );
    }

    switch (viewMode) {
      case 'table':
        return renderTableView();
      case 'bar':
      case 'line':
      case 'pie':
        return renderChartView();
      default:
        return renderTableView();
    }
  };

  return (
    <Card
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>Report Preview</Title>
          <Space size="small">
            {viewModes.map(mode => (
              <Button
                key={mode.key}
                type={viewMode === mode.key ? 'primary' : 'default'}
                icon={<mode.icon />}
                size="small"
                onClick={() => onViewModeChange && onViewModeChange(mode.key)}
              >
                {mode.label}
              </Button>
            ))}
          </Space>
        </Space>
      }
      style={{ height: '100%' }}
      bodyStyle={{
        height: 'calc(100% - 60px)',
        padding: '16px',
        overflow: 'hidden'
      }}
    >
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <Space direction="vertical" align="center">
            <Spin size="large" />
            <Typography.Text type="secondary">Loading report data...</Typography.Text>
          </Space>
        </div>
      ) : (
        <div style={{ height: '100%', overflow: 'auto' }}>
          {renderContent()}
        </div>
      )}

      <ChartConfigModal
        visible={chartModalVisible}
        onCancel={() => setChartModalVisible(false)}
        onSave={handleChartSave}
        data={data}
        initialConfig={chartConfig}
      />
    </Card>
  );
};

export default PreviewPanel;